import React, { startTransition, useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation  } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Core/i18n';
import { initLang, availableLanguages, normalizeTheme } from './Core/Config';
import { AnimatePresence } from 'framer-motion';
import { AnimatedRoute }   from './Utilites/AnimatedRoutes';
import SwitchTheme    from './Utilites/SwitchTheme';
import BackToHomepage from './Utilites/BackToHomepage';
import AppLoading from './Utilites/AppLoading';
import QuizHeader from './Scenes/Blocks/QuizHeader';
import { isAreaUnlocked, isLanguageUnlocked } from './Core/Auth';
import { fetchAreas, fetchGroups } from './Core/FetchData';
import LangSelection from './Scenes/LangSelection';
import GroupSelection from './Scenes/GroupSelection';
import AreaSelection from './Scenes/AreaSelection';
import QuizSelection from './Scenes/QuizSelection';
import LevelSelection from './Scenes/LevelSelection';
import Play from './Scenes/Play';

const findLanguage = (code) => {
    const languageCode = typeof code === 'string' ? code : code?.code;

    return availableLanguages.find((language) => language.code === languageCode)
        || availableLanguages.find((language) => language.code === initLang)
        || availableLanguages[0];
};

const findUnlockedLanguage = (auth) => availableLanguages.find(
    (language) => isLanguageUnlocked(language, auth)
) || findLanguage(initLang);

const hasAppDeepLink = (search) => {
    const params = new URLSearchParams(search);

    return Boolean(params.get('lang') && params.get('group'));
};

function Quiz({ auth }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { i18n } = useTranslation();
    const showHomepageLink = ['/quiz', '/quiz/select-group', '/quiz/select-area'].includes(
        location.pathname.replace(/\/+$/, '')
    );

    const [lang, setLang]   = useState(findLanguage(localStorage.getItem('quizLang') || initLang));
    const [theme, setTheme] = useState(() => normalizeTheme(localStorage.getItem('quizTheme')));
    const [group, setGroup] = useState([]);  
    const [area, setArea]   = useState([]); 
    const [quiz, setQuiz]   = useState([]); 
    const [level, setLevel] = useState(null);        
    const [handledDeepLink, setHandledDeepLink] = useState(null);
    const [deepLinkLoading, setDeepLinkLoading] = useState(() => hasAppDeepLink(window.location.search));

    const handleBackToSelection = (path) => {
        // Change route and dependent selections in one transition. AnimatePresence
        // can then finish the outgoing scene with its last valid props.
        startTransition(() => {
            if (path === '/quiz' || path === '/quiz/select-group') setGroup([]);
            if (path !== '/quiz/select-quiz') setArea([]);
            setQuiz([]);
            setLevel(null);
            navigate(path);
        });
    };

    // Lang switch
        useEffect(() => {
            const normalizedLang = findLanguage(lang);
            const toLang = normalizedLang?.code || initLang;

            if (normalizedLang && normalizedLang !== lang) {
                setLang(normalizedLang);
            }

            if (i18n.resolvedLanguage !== toLang) {
                i18n.changeLanguage(toLang);
            }
            localStorage.setItem('quizLang', toLang);
   
        }, [lang, i18n]);
   

    // Theme switch 
        useEffect(() => {
            localStorage.setItem('quizTheme', theme);
            document.getElementById("quiz-wrapper").setAttribute('data-theme', theme);
        }, [theme]);


    // Group select
        useEffect(() => {
            if (deepLinkLoading) {
                return;
            }

            if (group.id > 0 && !area?.id) {
                navigate('/quiz/select-area');
            }    
        }, [group.id, area?.id, deepLinkLoading, navigate]);  

    // Area select
        useEffect(() => {
            if (deepLinkLoading) {
                return;
            }

            if (area.id > 0) {
                navigate('/quiz/select-quiz');
            }    
        }, [area, deepLinkLoading, navigate]);  

    // Tariff guard
        useEffect(() => {
            if (deepLinkLoading) {
                return;
            }

            if (area?.id && !isAreaUnlocked(area, auth, lang, group)) {
                setArea([]);
                navigate('/quiz/select-area');
            }
        }, [area, auth, lang, group, deepLinkLoading, navigate]);

    // Language guard
        useEffect(() => {
            if (deepLinkLoading) {
                return;
            }

            if (lang?.code && !isLanguageUnlocked(lang, auth)) {
                const fallbackLang = findUnlockedLanguage(auth);

                if (fallbackLang !== lang) {
                    setLang(fallbackLang);
                }

                setGroup([]);
                setArea([]);

                if (location.pathname !== '/quiz') {
                    navigate('/quiz', { replace: true });
                }
            }
        }, [lang, auth, deepLinkLoading, location.pathname, navigate]);

    // Quiz/level select
        useEffect(() => {
            if (quiz.id > 0) {
                if (quiz.type === 'textCompletion') {
                    navigate('/quiz/select-level');
                } else {
                    navigate('/quiz/play');
                }
            }
        }, [quiz, navigate]);     

    // skip lang select if only one lang    
        useEffect(() => {
            if (deepLinkLoading) {
                return;
            }

            if (availableLanguages.length === 1) {
                setLang(availableLanguages[0]);
                navigate('/quiz/select-area');
            }
        }, [deepLinkLoading, navigate]);

    // Direct group or lesson links from the website:
    // /quiz?lang=en&group=... or /quiz?lang=en&group=...&area=...
        useEffect(() => {
            const params = new URLSearchParams(location.search);
            const languageCode = params.get('lang');
            const groupId = params.get('group');
            const areaId = params.get('area');
            const deepLinkKey = languageCode && groupId
                ? `${languageCode}:${groupId}:${areaId || ''}`
                : null;

            if (!deepLinkKey) {
                setDeepLinkLoading(false);
                return;
            }

            if (handledDeepLink === deepLinkKey) {
                return;
            }

            let active = true;
            setDeepLinkLoading(true);

            const openLinkedLesson = async () => {
                try {
                    const targetLang = findLanguage(languageCode);

                    if (!targetLang || !isLanguageUnlocked(targetLang, auth)) {
                        if (!active) {
                            return;
                        }

                        setHandledDeepLink(deepLinkKey);
                        navigate('/quiz', { replace: true });
                        setDeepLinkLoading(false);
                        return;
                    }

                    const groups = await fetchGroups(targetLang);
                    const targetGroup = groups.find((item) => String(item.id) === String(groupId));

                    if (!active) {
                        return;
                    }

                    if (!targetGroup) {
                        setHandledDeepLink(deepLinkKey);
                        navigate('/quiz/select-group', { replace: true });
                        setDeepLinkLoading(false);
                        return;
                    }

                    setLang(targetLang);
                    setGroup(targetGroup);

                    if (!areaId) {
                        setArea([]);
                        setQuiz([]);
                        setLevel(null);
                        setHandledDeepLink(deepLinkKey);
                        navigate('/quiz/select-area', { replace: true });
                        setDeepLinkLoading(false);
                        return;
                    }

                    const areas = await fetchAreas(targetLang, targetGroup);
                    const targetArea = areas.find((item) => String(item.id) === String(areaId));

                    if (!active) {
                        return;
                    }

                    if (!targetArea || !isAreaUnlocked(targetArea, auth, targetLang, targetGroup)) {
                        setArea([]);
                        setHandledDeepLink(deepLinkKey);
                        navigate('/quiz/select-area', { replace: true });
                        setDeepLinkLoading(false);
                        return;
                    }

                    setArea(targetArea);
                    setQuiz([]);
                    setLevel(null);
                    setHandledDeepLink(deepLinkKey);
                    navigate('/quiz/select-quiz', { replace: true });
                    setDeepLinkLoading(false);
                } catch (error) {
                    if (!active) {
                        return;
                    }

                    console.error('Failed to open linked lesson', error);
                    setHandledDeepLink(deepLinkKey);
                    navigate('/quiz/select-group', { replace: true });
                    setDeepLinkLoading(false);
                }
            };

            openLinkedLesson();

            return () => {
                active = false;
            };
        }, [auth, handledDeepLink, location.search, navigate]);


  return (

        <div id="quiz">
            <div id="quiz-bg"></div>

            <div id="quiz-inner">
                <QuizHeader
                    lang={lang}
                    group={group}
                    area={area}
                    quiz={quiz}
                    onBack={handleBackToSelection}
                    onSelectLanguage={() => handleBackToSelection('/quiz')}
                    location={location}
                />
             
                <div id="quiz-content" className="text-primary">
                    {deepLinkLoading ? (
                        <AppLoading>Načítám lekci...</AppLoading>
                    ) : (
                        <AnimatePresence mode='wait'>
                            <Routes location={location} key={location.pathname}>
                                <Route path="/" element={<AnimatedRoute duration={0.16}><LangSelection onChange={setLang} activeLang={lang || initLang} auth={auth} /></AnimatedRoute>} />
                                <Route path="select-group" element={<AnimatedRoute duration={0.16}><GroupSelection onChange={setGroup} lang={lang} /></AnimatedRoute>} />
                                <Route path="select-area" element={<AnimatedRoute duration={0.16}><AreaSelection onChange={setArea} lang={lang} group={group} auth={auth} /></AnimatedRoute>} />
                                <Route path="select-quiz" element={<AnimatedRoute><QuizSelection onChange={setQuiz} lang={lang} area={area} /></AnimatedRoute>} />
                                <Route path="select-level" element={<AnimatedRoute><LevelSelection area={area} onChange={setLevel} /></AnimatedRoute>} />
                                <Route path="play" element={<AnimatedRoute><Play lang={lang} group={group} area={area} quiz={quiz} level={level} onQuit={() => handleBackToSelection('/quiz/select-quiz')} auth={auth} /></AnimatedRoute>} />
                            </Routes>
                        </AnimatePresence>
                    )}
                </div>
                
                <div id="quiz-footer" className={showHomepageLink ? 'quiz-footer--selection' : undefined}>
                    <SwitchTheme onChange={setTheme} activeTheme={theme} />   
                    {showHomepageLink && <BackToHomepage />}
                    {/* <Settings /> odkomentovat az bude potreba  */}    
                </div>

            </div>
        </div>
  );
}

export default Quiz;

